import { type FC, type MutableRefObject, useCallback } from 'react';
import { useTranslation } from 'react-i18next';
import TextTemplater from 'components/Kizen/TextTemplater';
import { KizenTypography } from '__app/typography';
import {
  StyledPromptUsage,
  StyledOutlineInputControl,
  StyledPromptMessage,
} from './styles';
import type { ModelOption } from './types';
import type { WYSIWYGHandle } from 'components/WYSIWYG/types';

export type ChangePromptData = {
  htmlPrompt: string;
  prompt: string;
};

type LLMPromptProps = {
  onChange: (payload: ChangePromptData) => void;
  htmlPrompt: string;
  mergeFields: any[];
  prompt: string;
  model: ModelOption | undefined;
  noProviderOptions: boolean;
  renderTokenMessage?: ({
    tokenMessage,
  }: {
    tokenMessage: string;
  }) => JSX.Element;
  disabled: boolean;
  placeholder?: string;
  isDecision?: boolean;
  editorRef?: MutableRefObject<WYSIWYGHandle | null>;
};

export const LLMPrompt: FC<LLMPromptProps> = ({
  onChange,
  mergeFields,
  htmlPrompt,
  prompt,
  model,
  renderTokenMessage = ({ tokenMessage }) => tokenMessage,
  disabled,
  noProviderOptions,
  placeholder,
  isDecision = false,
  editorRef,
}) => {
  const { t } = useTranslation();

  const onSave = useCallback(
    (payload: { htmlContent: string; textContent: string }) =>
      onChange({
        htmlPrompt: payload.htmlContent,
        prompt: payload.textContent || '',
      }),
    [onChange]
  );

  if (noProviderOptions) {
    return (
      <StyledPromptMessage>
        <div className="button-wrapper">
          <KizenTypography
            type="anchor"
            href={`/marketplace`}
            target="_blank"
            rel="noreferrer"
          >
            {t('Configure LLM Providers')}
          </KizenTypography>
        </div>
      </StyledPromptMessage>
    );
  }

  return (
    <>
      <TextTemplater
        editorRef={editorRef}
        appendMergeFieldExtraSpace={false}
        title={<StyledOutlineInputControl label={t('Prompt')} />}
        defaultValue={htmlPrompt}
        onSave={onSave}
        textareaOptions={{
          placeholder:
            placeholder ??
            t(
              'Create a concise notification message for my business administrator.'
            ),
          enableResize: false,
          initialHeight: 136,
          borderHoverColor: 'var(--color-button-secondary-hover)',
        }}
        customize={{
          mergeFields,
          autoSuggestions: true,
          wrapperClassName: 'templater',
          showCountComponent: false,
          disableFocus: true,
        }}
        encodeMergeFieldsInBody
        dataQA="ehr-data-payload"
        disabled={disabled || noProviderOptions}
        toggleDisabled={() => {}}
      />
      <StyledPromptUsage>
        <KizenTypography>
          {t('Maximum input tokens for this model: {{maxInputTokens}}', {
            maxInputTokens: model?.model?.max_input_tokens || '---',
          })}
        </KizenTypography>
        <KizenTypography>
          {renderTokenMessage({
            tokenMessage: t('{{tokens}} tokens', {
              tokens: model
                ? Math.max(
                    0,
                    Math.round(
                      ((isDecision && model.model.decision_token_count_base) ||
                        0) +
                        prompt.length / model.model.avg_char_per_token
                    )
                  )
                : 0,
            }),
          })}
        </KizenTypography>
      </StyledPromptUsage>
    </>
  );
};
